import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { routeUrls } from "../../../navigation/routeUrls";
import { APP_CONFIG } from "../../../constants/constants";
import {
  setListingQueryParams,
  setModalsData,
  TListingQueryParams,
  TModalsState,
} from "../../../api/store/commonSlice";
import { useDebouncedCallback } from "use-debounce";
import React, { useEffect, useMemo, useState } from "react";
import AppSearchBox from "../../../components/searchBox";
import AdminListingColumnItem from "../../../components/adminListingColumnItem";
import Pagination, {
  TPaginationSelected,
} from "../../../components/pagination";
import {
  AdminFormFieldSubmit,
  TSelectboxOption,
} from "../../../components/admin/formFields";

import { useFormik } from "formik";
import FillAllRequiredFieldsCaption from "../../../components/fillAllRequiredFieldsCaption";
import Accordian from "../../../components/accordian";
import {
  RuleAdvanceDetailForm,
  RuleGeneralDetailForm,
  RuleStatusCodeDetailForm,
} from "./rule-form";
import HeaderView from "../../../components/admin/headerView";
import {
  useDeleteOrganiaztionRuleMutation,
  useEditOrganizationRuleAlertMutation,
  useOrganizationRuleAlertListQuery,
  useSingleOrganizationRuleAlertQuery,
} from "../../../api/network/adminApiServices";
import { LegacyRuleWithAccount, TLegacyRule } from "../../../api/types/Rule";
import { ruleMessageFields } from "./rule-message-fields";
import { toast } from "react-toastify";
import { serializeErrorKeyValues } from "../../../api/network/errorCodes";
import {
  ruleDetailsInitialValues,
  ruleDetailsYupValidationSchema,
  TFormFieldNames,
} from "./validation";
import ModalDeleteConfirmation from "../../../components/admin/modalDeleteConfirmation";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { RESPONSE_CODE } from "../../../api/network/constants";
import svgIcons from "../../../assets/svg/svgIcons";
import { ACL_NAMES, useACL } from "../../../hooks/acl";
import { ROLE_ACCESS_LEVELS } from "../role/constants";
import FormActionButtons from "../../../components/admin/formActionButtons";

const ScreenManageRuleAlertDetails = () => {
  const { ruleId } = useParams<{ ruleId: any }>();
  const { state: locationState } = useLocation(); // OrganizationEntityListingPayload | { new: true }
  const { t: tMain } = useTranslation();
  const { t } = useTranslation("translation", {
    keyPrefix: "manageRuleAlert.detailsPage",
  });
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { getUserACLAccessLevel } = useACL();

  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );
  const listingQueryParams: TListingQueryParams = useSelector(
    (state: any) => state.commonReducer.listingQueryParams
  );
  const { rules: orgRulesQueryParams } = listingQueryParams;

  // flag to idenfiy if user is coming from create new user popup
  const isNewEntity = React.useRef<boolean>(!!locationState?.new);

  // flag to enable edit mode
  const [userCanEdit, setUserCanEdit] = useState<boolean>(
    !!isNewEntity?.current
  );

  const debouncedSetSearchKeyword = useDebouncedCallback((value: string) => {
    dispatch(
      setListingQueryParams({
        ...listingQueryParams,
        rules: { ...orgRulesQueryParams, page: 1, search: value },
      })
    );
  }, 500);

  // fetch organization rule
  const {
    data: dataOrgRules,
    isFetching: isFetchingOrgRules,
    error,
  } = useOrganizationRuleAlertListQuery(orgRulesQueryParams);
  const { results, count } = dataOrgRules || {};

  // fetch single rule details
  const {
    data: dataSingleRule,
    isFetching: isFetchingSingleRule,
    isError: isErrorSingleEntity,
    error: errorSingleEntity,
  } = useSingleOrganizationRuleAlertQuery(
    {
      ruleid: `${ruleId}`,
    },
    {
      skip: !ruleId,
      refetchOnMountOrArgChange: true, // prevent cached values from displaying when creating entity with same id
    }
  );
  useEffect(() => {
    if (isErrorSingleEntity) {
      if (
        (errorSingleEntity as FetchBaseQueryError)?.status ===
        RESPONSE_CODE.NOT_FOUND
      ) {
        toast.error(t("toast.rule_not_found"));
        navigate(routeUrls.dashboardChildren.adminChildren.manage_rule_alert);
      }
    }
  }, [isErrorSingleEntity, errorSingleEntity]);

  // rule mutations
  const [editOrganizationRuleApiTrigger, { isLoading: isLoadingEditRule }] =
    useEditOrganizationRuleAlertMutation();
  const [deleteSingleRuleApiTrigger, { isLoading: isLoadingDeleteRule }] =
    useDeleteOrganiaztionRuleMutation();

  // formik
  const [formikValuesReady, setFormikValuesReady] = useState<boolean>(false);
  useEffect(() => {
    setFormikValuesReady(!isFetchingSingleRule);
  }, [isFetchingSingleRule]);

  const formik = useFormik({
    initialValues: ruleDetailsInitialValues,
    validationSchema: ruleDetailsYupValidationSchema,
    onSubmit: (values) => {
      // prepare payload
      const preparedRuleEditParam: Partial<LegacyRuleWithAccount> = {
        ruleid: values?.ruleid,
        actionmask:
          // refer validation.ts for the following values
          values?.is_email_sms && values?.is_save_alert && values?.is_queue
            ? 66311
            : values?.is_email_sms && values?.is_save_alert
              ? 65799
              : values?.is_save_alert && values?.is_queue
                ? 66048
                : values?.is_email_sms && values?.is_queue
                  ? 775
                  : values?.is_email_sms
                    ? 263
                    : values?.is_save_alert
                      ? 65536
                      : values?.is_queue
                        ? 512
                        : 0,
        selector: values?.selector,
        isactive: values?.is_active ? 1 : 0,
        cron_flag: values?.cron_rule,
        description: values?.description,
        minnotifyage: values?.minimum_interval,
        triggerreset: values?.interval_reset ? 1 : 0,
        cannedactions: values?.predefined_actions,
        notification_email_sms: {
          "email": values?.notification_email.length > 0 ? values?.notification_email:undefined,
          "sms": values?.notification_phone.length > 0 ? values?.notification_phone:undefined
        },
        notifytype:
          // refer validation.ts for the following values
          values?.via_notifications && values?.via_alerts
            ? 2
            : values?.via_notifications
              ? 0
              : values?.via_alerts
                ? 1
                : null,
        emailsubject: values?.email_subject,
        emailtext: values?.email_message,
        smstext: values?.sms_message,
        rulelist: {
          deviceid: values?.isassets && !!values?.assets ? values?.assets : "-",
          groupid: values?.isgroups && !!values?.groups ? values?.groups : "-",
          accountid: "",
          ruleid: "",
        },
      };

      // call api for updating rule
      editOrganizationRuleApiTrigger(preparedRuleEditParam)
        .unwrap()
        .then(() => {
          toast.success(t("toast.rule_updated"));
          navigate(routeUrls.dashboardChildren.adminChildren.manage_rule_alert);
        })
        .catch((error) => {
          const errors = !!error?.data
            ? serializeErrorKeyValues(error?.data)
            : [t("toast.updation_failed")];
          toast.error(errors?.join(" "));
        });
    },
  });

  // pre-fill formik values
  useEffect(() => {
    if (dataSingleRule) {
      setFormikValuesReady(false); // simulate render delay for select pre-selected values
      formik.setValues({
        ruleid: dataSingleRule?.ruleid,
        selector: dataSingleRule?.selector,
        cron_rule: dataSingleRule?.cron_flag?.toString(),
        is_active: dataSingleRule?.isactive === 1,
        description: dataSingleRule?.description,
        interval_reset: dataSingleRule?.triggerreset === 1,
        minimum_interval: dataSingleRule?.minnotifyage,
        predefined_actions: dataSingleRule?.cannedactions,
        // refer validation.ts for the following values
        is_email_sms: [263, 65799, 66311, 775].includes(
          dataSingleRule?.actionmask
        ),
        is_save_alert: [65799, 66311, 65536, 66048].includes(
          dataSingleRule?.actionmask
        ),
        is_queue: [66311, 66048, 512, 775].includes(dataSingleRule?.actionmask),
        via_notifications:
          dataSingleRule?.notifytype !== null &&
          [0, 2].includes(dataSingleRule?.notifytype),
        via_alerts:
          dataSingleRule?.notifytype !== null &&
          [1, 2].includes(dataSingleRule?.notifytype),
        email: "",
        phone: "",
        notification_email: dataSingleRule?.notification_email_sms?.email || [],
        notification_phone: dataSingleRule?.notification_email_sms?.sms || [],

        ...ruleMessageFields(dataSingleRule),

        isassets: dataSingleRule?.rulelist?.deviceid
          ? dataSingleRule?.rulelist?.deviceid !== "-"
          : false,
        isgroups: dataSingleRule?.rulelist?.groupid
          ? dataSingleRule?.rulelist?.groupid !== "-"
          : false,
        assets: dataSingleRule?.rulelist?.deviceid || "-",
        groups: dataSingleRule?.rulelist?.groupid || "-",
      });

      setUserCanEdit(!!isNewEntity?.current);
      setTimeout(() => {
        setFormikValuesReady(true);
      }, 200); // simulate render delay for select pre-selected values
    }
  }, [dataSingleRule, ruleId]);

  const updateFormikValues = (values: TLegacyRule | undefined) => {
    if (values) {
      formik.setValues((prevValues) => ({
        ...prevValues, // Spread the previous values to keep unchanged fields
        selector: values?.selector,
        is_active: values?.isactive === 1,
        predefined_actions: values?.cannedactions,
        description: values?.description,
        minimum_interval: values?.minnotifyage,
        interval_reset: values?.triggerreset === 1,
        ...ruleMessageFields(values),
      }));
    }
  };

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    dirty,
  } = formik;

  const invalidFields = Object.keys(errors).filter(
    (key) => !!errors[key as TFormFieldNames]
  );
  const isFormValid = invalidFields.length === 0 && dirty;

  // handle edit user
  const handleEditUser = () => {
    if (userCanEdit) {
      formik.handleSubmit();
    }
  };

  // handle delete rule
  const handleDeleteRule = () => {
    deleteSingleRuleApiTrigger({
      ruleid: ruleId,
      accountid: `${dataSingleRule?.accountid}`,
    })
      .unwrap()
      .then(() => {
        toast.success(t("toast.rule_deleted"));
        navigate(routeUrls.dashboardChildren.adminChildren.manage_rule_alert);
      })
      .catch((error) => {
        const errors = !!error?.data
          ? serializeErrorKeyValues(error?.data)
          : [t("toast.deletion_failed")];
        toast.error(errors?.join(" "));
      });
  };

  const MemoizedFormActionButtons = useMemo(() => {
    return (
      <>
        <FormActionButtons
          buttons={[
            {
              customWrapperClass: "w-24",
              type: "button",
              variant: "danger",
              label: tMain("delete"),
              onClick: () => {
                dispatch(
                  setModalsData({
                    ...modalsState,
                    showDeleteConfirmation: true,
                  })
                );
              },
              disabled:
                isLoadingEditRule ||
                getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_RULE) <
                  ROLE_ACCESS_LEVELS.ALL,
            },
            {
              customWrapperClass: "w-24",
              type: "button",
              variant: "primary",
              label: userCanEdit ? tMain("update") : tMain("edit"),
              onClick: userCanEdit
                ? handleEditUser
                : () => setUserCanEdit(!userCanEdit),
              disabled:
                isLoadingEditRule ||
                (userCanEdit && !!Object.keys(errors).length) ||
                getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_RULE) <
                  ROLE_ACCESS_LEVELS.WRITE,
            },
          ]}
        />
      </>
    );
  }, [modalsState, isLoadingEditRule, userCanEdit, errors]);
  const MemoizedFormActionSaveButton = useMemo(() => {
    return (
      <>
        <FormActionButtons
          buttons={[
            {
              type: "submit",
              variant: "success",
              label: tMain("save"),
              onClick: () => handleEditUser(),
              disabled: isLoadingEditRule || !!Object.keys(errors).length,
            },
          ]}
        />
      </>
    );
  }, [isLoadingEditRule, errors]);
  return (
    <>
      <HeaderView
        title={t("heading")}
        showBackButton={true}
        backButtonCallback={() =>
          navigate(routeUrls.dashboardChildren.adminChildren.manage_rule_alert)
        }
      />
      <div className={`${APP_CONFIG.DES.DASH.P_HORIZ} py-2`}>
        <div className="flex items-center">
          <p className="font-semibold text-accent-blue-dark text-lg leading-6">
            {t("sub_heading")}
          </p>
        </div>
        <div className="lg:grid lg:grid-cols-12 mt-8 py-2 gap-4">
          <div
            className={`hidden xl:block lg:col-span-3 space-y-4${
              isFetchingOrgRules ? " opacity-40 pointer-events-none" : ""
            }${isNewEntity?.current ? " xl:hidden" : ""}`}
          >
            <div className="font-bold text-lg leading-6 mt-2 mb-3">
              {t("listing_heading")}
            </div>
            <AppSearchBox
              placeholder={t("search_placeholder")}
              initialValue={orgRulesQueryParams.search}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                debouncedSetSearchKeyword(e.target.value)
              }
              onClear={() => {
                debouncedSetSearchKeyword("");
              }}
            />
            <div className="max-h-admins overflow-y-auto">
              {dataOrgRules?.results?.map(
                (item: TLegacyRule, index: number) => (
                  <AdminListingColumnItem
                    key={index}
                    selected={ruleId === item.ruleid}
                    onClick={() =>
                      navigate(
                        `${routeUrls.dashboardChildren.adminChildren.manage_rule_alert}/${item.ruleid}`
                      )
                    }
                    title={item.ruleid}
                    description={item.description}
                    asideText={item.isactive === 1 ? "Yes" : "No"}
                    bottomText={item.ruletag ? item.ruletag.toString() : "-"}
                  />
                )
              )}
            </div>
            {!isFetchingOrgRules && (
              <Pagination
                pageSize={orgRulesQueryParams.page_size}
                handlePageSizeChange={(e: TSelectboxOption | null) => {
                  dispatch(
                    setListingQueryParams({
                      ...listingQueryParams,
                      rules: {
                        ...orgRulesQueryParams,
                        page: 1,
                        page_size: parseInt(`${e?.value}`),
                      },
                    })
                  );
                }}
                totalPages={
                  count ? Math.ceil(count / orgRulesQueryParams.page_size) : 1
                }
                forcePage={orgRulesQueryParams.page - 1}
                handlePageClick={(data: TPaginationSelected) => {
                  dispatch(
                    setListingQueryParams({
                      ...listingQueryParams,
                      rules: {
                        ...orgRulesQueryParams,
                        page: data?.selected + 1,
                      },
                    })
                  );
                }}
                onlyPageChange={true}
                size={1}
              />
            )}
          </div>
          <div
            className={`${isNewEntity?.current ? "lg:col-span-12" : "col-span-12 xl:col-span-9"}`}
          >
            <div className="flex items-center gap-4">
              {isNewEntity?.current ? (
                <>
                  <p className="font-semibold text-accent-blue-dark text-base leading-6">
                    {tMain("admins.completeCreation")}
                  </p>
                  <div className="flex-grow"></div>
                  {isFetchingSingleRule && (
                    <img
                      src={svgIcons.LoadingAnimation}
                      alt="loading-animation"
                      className="w-8 h-8"
                    />
                  )}
                  {userCanEdit && !!Object.keys(errors).length && (
                    <FillAllRequiredFieldsCaption />
                  )}
                  <div className="w-24">
                    {/* <AdminFormFieldSubmit
                      type="submit"
                      variant="success"
                      label={tMain("save")}
                      onClick={handleEditUser}
                      disabled={isLoadingEditRule || !!Object.keys(errors).length}
                    /> */}
                    {MemoizedFormActionSaveButton}
                  </div>
                </>
              ) : (
                <>
                  <div className="flex-grow"></div>
                  {isFetchingSingleRule && (
                    <img
                      src={svgIcons.LoadingAnimation}
                      alt="loading-animation"
                      className="w-8 h-8"
                    />
                  )}
                  {userCanEdit && !!Object.keys(errors).length && (
                    <FillAllRequiredFieldsCaption />
                  )}
                  {/* <div className="w-24 ">
                     <AdminFormFieldSubmit
                      type="button"
                      variant="danger"
                      label={tMain("delete")}
                      onClick={() => { dispatch(setModalsData({ ...modalsState, showDeleteConfirmation: true })) }}
                      disabled={isLoadingEditRule || getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_RULE) < ROLE_ACCESS_LEVELS.ALL}
                    /> 
                    </div>  */}
                  {MemoizedFormActionButtons}
                  {/* <div className="w-24">
                    <AdminFormFieldSubmit
                      type="button"
                      variant="primary"
                      label={userCanEdit ? tMain("update") : tMain("edit")}
                      onClick={userCanEdit ? handleEditUser : () => setUserCanEdit(!userCanEdit)}
                      disabled={isLoadingEditRule || (userCanEdit && !!Object.keys(errors).length) || getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_RULE) < ROLE_ACCESS_LEVELS.WRITE}
                    />
                  </div> */}
                </>
              )}
            </div>
            <div
              className={`rounded-lg mt-2 bg-accent-blue-pale transition ${isFetchingSingleRule || isLoadingEditRule || isLoadingDeleteRule ? "opacity-40" : ""}`}
            >
              <form onSubmit={handleSubmit}>
                <Accordian
                  title={t("accord_general_details")}
                  openByDefault={true}
                >
                  <RuleGeneralDetailForm
                    values={values}
                    errors={errors}
                    touched={touched}
                    handleChange={handleChange}
                    formikSetValue={formik.setFieldValue}
                    handleBlur={handleBlur}
                    formikSetTouched={formik.setFieldTouched}
                    formik={formik}
                    userCanEdit={userCanEdit}
                    loadingData={isFetchingSingleRule || !formikValuesReady}
                    updateFormikValues={updateFormikValues}
                  />
                </Accordian>
                <Accordian title={t("accord_advance_details")}>
                  <RuleAdvanceDetailForm
                    values={values}
                    errors={errors}
                    touched={touched}
                    handleChange={handleChange}
                    formikSetValue={formik.setFieldValue}
                    handleBlur={handleBlur}
                    formik={formik}
                    formikSetTouched={formik.setFieldTouched}
                    userCanEdit={userCanEdit}
                    loadingData={isFetchingSingleRule || !formikValuesReady}
                  />
                </Accordian>
                <Accordian title={t("accord_status_code_selection_details")}>
                  <RuleStatusCodeDetailForm
                    values={values}
                    errors={errors}
                    touched={touched}
                    handleChange={handleChange}
                    formikSetValue={formik.setFieldValue}
                    handleBlur={handleBlur}
                    formikSetTouched={formik.setFieldTouched}
                    userCanEdit={userCanEdit}
                    loadingData={isFetchingSingleRule || !formikValuesReady}
                  />
                </Accordian>
              </form>
            </div>
          </div>
        </div>
      </div>
      <ModalDeleteConfirmation handleDeleteAdmin={handleDeleteRule} />
    </>
  );
};

export default ScreenManageRuleAlertDetails;
